import { convertFormDateToUTC5 } from '../../../utils/dateUtils';
import { filterEmptyArrayItems } from '../../../utils/validation';

/**
 * Helper function to extract file keys from image objects
 * Images can come as objects with {id, url, key, file, ...} or as strings (keys)
 * Returns array of keys (strings) - File objects are handled separately
 * 
 * IMPORTANT: We store only S3 keys in DB, NOT signed URLs
 */
export const extractImageKeys = (images) => {
  if (!Array.isArray(images)) return [];

  const extractKeyFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;

    if (url.startsWith('blob:')) return null;

    if (url.startsWith('/api/files/') || url.startsWith('/files/')) {
      let path = url.replace(/^\/api\/files\//, '').replace(/^\/files\//, '');
      if (path.startsWith('signed-url/')) {
        path = path.replace(/^signed-url\//, '');
        try {
          return decodeURIComponent(path.split('?')[0]);
        } catch (e) {
          return path.split('?')[0];
        }
      }
      return path;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const parsed = new URL(url);
        let path = parsed.pathname || '';
        if (path.startsWith('/')) path = path.slice(1);

        if (path.startsWith('api/files/signed-url/') || path.startsWith('files/signed-url/')) {
          path = path.replace(/^(api\/)?files\/signed-url\//, '');
          try {
            return decodeURIComponent(path.split('?')[0]);
          } catch (e) {
            return path.split('?')[0];
          }
        }

        if (path.startsWith('api/files/')) {
          path = path.replace(/^api\/files\//, '');
        }
        if (path.startsWith('files/')) {
          path = path.replace(/^files\//, '');
        }

        if (parsed.hostname && parsed.hostname.includes('.amazonaws.com')) {
          if (path.includes('/')) {
            const parts = path.split('/');
            if (parts.length > 1) {
              return parts.slice(1).join('/') || path;
            }
          }
        }

        return path || null;
      } catch (e) {
        // Not a valid URL - might be a key already
        // Remove /api/files/ prefix if present
        let key = url;
        if (key.startsWith('/api/files/')) {
          key = key.replace(/^\/api\/files\//, '');
        }
        if (key.startsWith('/files/')) {
          key = key.replace(/^\/files\//, '');
        }
        // Remove query params if any
        if (key.includes('?')) {
          key = key.split('?')[0];
        }
        return key || null;
      }
    }
    
    // Already a key (doesn't start with http/https/blob)
    // Remove query params if any
    if (url.includes('?')) {
      return url.split('?')[0];
    }
    
    return url;
  };

  return images.map(img => {
    if (typeof img === 'string') {
      if (img.startsWith('blob:')) {
        return null;
      }
      return extractKeyFromUrl(img);
    }

    if (img && typeof img === 'object' && img.key) {
      return img.key;
    }

    if (img && typeof img === 'object' && img.url) {
      if (img.url.startsWith('blob:')) {
        return null;
      }
      return extractKeyFromUrl(img.url);
    }

    if (img && typeof img === 'object' && img.file instanceof File) {
      return null;
    }

    return null;
  }).filter(key => key !== null && key !== '');
};

/**
 * Legacy function name for backward compatibility
 * @deprecated Use extractImageKeys instead
 */
const extractImageUrls = extractImageKeys;

/**
 * Helper function to extract File objects from image arrays
 * Returns array of File objects that need to be uploaded
 */
const extractFileObjects = (images, fieldName) => {
  if (!Array.isArray(images)) return [];
  
  const files = [];
  images.forEach((img, index) => {
    // If it's an object with file property (File object)
    if (img && typeof img === 'object' && img.file instanceof File) {
      files.push({
        file: img.file,
        fieldName: fieldName,
        index: index
      });
    }
  });
  
  return files;
};

/**
 * Check if form data contains File objects that need to be uploaded
 * @param {Object} formData - Form data object
 * @returns {Boolean} - True if contains File objects
 */
export const hasFileObjects = (formData) => {
  const checkArray = (arr) => {
    if (!Array.isArray(arr)) return false;
    return arr.some(item => 
      item && typeof item === 'object' && item.file instanceof File
    );
  };
  
  return (
    checkArray(formData.vehicle?.images) ||
    checkArray(formData.vehicle?.vehicleImages) ||
    checkArray(formData.vehicle?.pdfs) ||
    checkArray(formData.freight?.images) ||
    checkArray(formData.freight?.freightImages) ||
    checkArray(formData.freight?.pdfs) ||
    checkArray(formData.pickup?.images) ||
    checkArray(formData.delivery?.images) ||
    checkArray(formData.carrierPhotos) ||
    checkArray(formData.bolDocuments) ||
    checkArray(formData.rateConfirmationDocuments) ||
    checkArray(formData.documents) ||
    checkArray(formData.pdfs)
  );
};

/**
 * Extract all File objects from form data for FormData upload
 * @param {Object} formData - Form data object
 * @returns {Array} - Array of {fieldName, file} objects
 */
export const extractAllFileObjects = (formData) => {
  const files = [];
  
  // Helper to add files from array
  const addFilesFromArray = (arr, fieldName) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((item, index) => {
      if (item && typeof item === 'object' && item.file instanceof File) {
        files.push({
          fieldName: fieldName,
          file: item.file,
          index: index
        });
      }
    });
  };
  
  // Extract files from all possible locations
  // Support both new (images) and legacy (vehicleImages) field names
  addFilesFromArray(formData.vehicle?.images, 'vehicleImages');
  addFilesFromArray(formData.vehicle?.vehicleImages, 'vehicleImages');
  addFilesFromArray(formData.vehicle?.pdfs, 'vehiclePdfs');
  addFilesFromArray(formData.freight?.images, 'freightImages');
  addFilesFromArray(formData.freight?.freightImages, 'freightImages');
  addFilesFromArray(formData.freight?.pdfs, 'freightPdfs');
  addFilesFromArray(formData.pickup?.images, 'pickupImages');
  addFilesFromArray(formData.delivery?.images, 'deliveryImages');
  addFilesFromArray(formData.carrierPhotos, 'carrierPhotos');
  addFilesFromArray(formData.bolDocuments, 'bolDocuments');
  addFilesFromArray(formData.rateConfirmationDocuments, 'rateConfirmationDocuments');
  addFilesFromArray(formData.documents, 'documents');
  addFilesFromArray(formData.pdfs, 'pdfs');
  
  return files;
};

/**
 * Transform form data to API format
 * Since form data now matches API structure, we mainly need to:
 * 1. Ensure all strings are properly converted
 * 2. Format dates to UTC-5 ISO format
 * 3. Extract image URLs from image objects
 * 4. Handle carrier.id validation
 */
export const transformLoadDataForAPI = (formData) => {
  // Helper to safely convert to string
  const safeString = (value) => String(value || '');
  
  // Helper to safely convert array
  const safeArray = (arr) => Array.isArray(arr) ? arr : [];
  
  const normalizeCarrierEquipment = (carrier) => {
    const equipment = safeArray(carrier?.equipment || []).map(item => ({
      type: safeString(item?.type).trim(),
      sizes: safeArray(item?.sizes || []).map(size => safeString(size).trim()).filter(Boolean)
    })).filter(item => item.type);

    const equipmentType = equipment.map(item => item.type);
    const size = Array.from(new Set(equipment.flatMap(item => item.sizes || [])));

    return { equipment, equipmentType, size };
  };
  
  // Handle carrier.id - validate MongoDB ObjectId format
  const isMongoId = (id) => /^[0-9a-fA-F]{24}$/.test(String(id || ''));
  let carrierId = null;
  if (formData.carrier?.id) {
    const idStr = String(formData.carrier.id);
    if (isMongoId(idStr)) {
      carrierId = idStr;
    }
  }
  
  // Extract image keys for vehicle and freight (support both field names)
  const vehicleImages = extractImageKeys(
    formData.vehicle?.vehicleImages || 
    formData.vehicle?.images || 
    []
  );
  
  const freightImages = extractImageKeys(
    formData.freight?.freightImages || 
    formData.freight?.images || 
    []
  );
  
  // Build the load data object - data structure matches API expectations
  const loadData = {
    orderId: safeString(formData.orderId),
    
    customer: {
      companyName: safeString(formData.customer?.companyName),
      type: safeString(formData.customer?.type || 'customer'),
      email: safeString(formData.customer?.email || ''),
      ...(formData.customer?.type !== 'platform' && {
        customerAddress: {
          address: safeString(formData.customer?.customerAddress?.address),
          city: safeString(formData.customer?.customerAddress?.city),
          state: safeString(formData.customer?.customerAddress?.state),
          zipCode: safeString(formData.customer?.customerAddress?.zipCode)
        },
      }),
      phoneNumber: safeString(formData.customer?.phoneNumber),
      // Don't include representativePeoples here - it will be saved in loadCustomerRepresentativePeoples
    },
    customerEmails: safeArray(formData.customerEmails)
      .map(email => safeString(email))
      .filter(email => email !== ''),
    customerRate: safeString(formData.customerRate),
    carrierRate: safeString(formData.carrierRate),
    
    type: {
      freight: Boolean(formData.type?.freight),
      vehicle: Boolean(formData.type?.vehicle)
    },
    
    vehicle: {
      shipment: safeArray(formData.vehicle?.shipment).map(v => ({
        vin: safeString(v.vin),
        make: safeString(v.make),
        model: safeString(v.model),
        year: safeString(v.year),
        value: safeString(v.value)
      })),
      specialRequirements: safeString(formData.vehicle?.specialRequirements),
      // Only send vehicleImages (not images) - API expects vehicleImages
      vehicleImages: vehicleImages
    },
    
    freight: {
      shipment: safeArray(formData.freight?.shipment).map(f => ({
        commodity: safeString(f.commodity),
        dimensionsLength: safeString(f.dimensionsLength),
        dimensionsWidth: safeString(f.dimensionsWidth),
        dimensionsHeight: safeString(f.dimensionsHeight),
        dimensionsUnit: (f.dimensionsUnit === 'inches' ? 'inches' : 'feet'),
        onPallets: f.onPallets === 'yes',
        weight: safeString(f.weight),
        shipmentUnits: (f.shipmentUnits === 'N/A' || (f.shipmentUnits && String(f.shipmentUnits).trim() !== '')) ? safeString(f.shipmentUnits) : undefined,
        poNumber: safeString(f.poNumber),
        pickupNumber: safeString(f.pickupNumber),
        deliveryReference: safeString(f.deliveryReference)
      })),
      // Only send freightImages (not images) - API expects freightImages
      freightImages: freightImages
    },
    
    pickup: {
      locationName: safeString(formData.pickup?.locationName),
      address: {
        address: safeString(formData.pickup?.address?.address),
        city: safeString(formData.pickup?.address?.city),
        state: safeString(formData.pickup?.address?.state),
        zipCode: safeString(formData.pickup?.address?.zipCode)
      },
      contactPhone: safeString(formData.pickup?.contactPhone),
      notes: safeString(formData.pickup?.notes),
      date: formData.pickup?.date || formData.dates?.pickupDate ? convertFormDateToUTC5(formData.pickup?.date || formData.dates?.pickupDate) : '',
      images: extractImageKeys(formData.pickup?.images || [])
    },
    
    delivery: {
      locationName: safeString(formData.delivery?.locationName),
      address: {
        address: safeString(formData.delivery?.address?.address),
        city: safeString(formData.delivery?.address?.city),
        state: safeString(formData.delivery?.address?.state),
        zipCode: safeString(formData.delivery?.address?.zipCode)
      },
      contactPhone: safeString(formData.delivery?.contactPhone),
      notes: safeString(formData.delivery?.notes),
      date: formData.delivery?.date || formData.dates?.deliveryDate ? convertFormDateToUTC5(formData.delivery?.date || formData.dates?.deliveryDate) : '',
      images: extractImageKeys(formData.delivery?.images || [])
    },
    
    carrier: {
      id: carrierId,
      name: safeString(formData.carrier?.name),
      // type field removed - use people array instead
      // Don't include people here - it will be saved in loadCarrierPeople (separate field)
      phoneNumber: safeString(formData.carrier?.phoneNumber),
      email: safeString(formData.carrier?.email),
      companyName: safeString(formData.carrier?.companyName),
      dba: safeString(formData.carrier?.dba),
      mcNumber: safeString(formData.carrier?.mcNumber),
      dotNumber: safeString(formData.carrier?.dotNumber),
      address: {
        address: safeString(formData.carrier?.address?.address),
        city: safeString(formData.carrier?.address?.city),
        state: safeString(formData.carrier?.address?.state),
        zipCode: safeString(formData.carrier?.address?.zipCode)
      },
      ...normalizeCarrierEquipment(formData.carrier || {}),
      capabilities: safeArray(formData.carrier?.capabilities || []).map(c => safeString(c)),
      certifications: safeArray(formData.carrier?.certifications || []).map(c => safeString(c))
    },
    carrierEmails: safeArray(formData.carrierEmails)
      .map(email => safeString(email))
      .filter(email => email !== ''),
    carrierPhotos: extractImageKeys(formData.carrierPhotos || []),
    
    paymentMethod: (() => {
      const selectedMethod = safeString(formData.paymentMethod);
      const otherMethod = safeString(formData.paymentMethodOther);
      if (selectedMethod === 'Other' && otherMethod) {
        return otherMethod;
      }
      return selectedMethod;
    })(),
    paymentTerms: safeString(formData.paymentTerms),
    
    insurance: {
      type: safeString(formData.insurance?.type || 'Motor Truck Cargo Coverage'),
      customAmount: safeString(formData.insurance?.customAmount || '')
    },
    
    status: safeString(formData.status || 'Listed'),
    
    dates: (() => {
      const pickupDateType = safeString(formData.dates?.pickupDateType || 'Exact');
      const deliveryDateType = safeString(formData.dates?.deliveryDateType || 'Exact');
      
      const datesObj = {
        pickupDateType: pickupDateType,
        deliveryDateType: deliveryDateType
      };
      
      // Add assignedDate only if provided
      if (formData.dates?.assignedDate) {
        datesObj.assignedDate = convertFormDateToUTC5(formData.dates.assignedDate);
      }
      
      // Add deadline only if provided
      if (formData.dates?.deadline) {
        datesObj.deadline = convertFormDateToUTC5(formData.dates.deadline);
      }
      
      // Add aging only if provided
      if (formData.dates?.aging && formData.dates.aging.trim() !== '') {
        datesObj.aging = safeString(formData.dates.aging);
      }
      
      // Pickup date handling based on type
      if (pickupDateType === 'Estimate') {
        // For Estimate, only send start and end dates
        if (formData.dates?.pickupDateStart) {
          datesObj.pickupDateStart = convertFormDateToUTC5(formData.dates.pickupDateStart);
        }
        if (formData.dates?.pickupDateEnd) {
          datesObj.pickupDateEnd = convertFormDateToUTC5(formData.dates.pickupDateEnd);
        }
      } else {
        // For Exact, only send pickupDate (use only dates.pickupDate, not pickup.date)
        if (formData.dates?.pickupDate) {
          datesObj.pickupDate = convertFormDateToUTC5(formData.dates.pickupDate);
        }
      }
      
      // Delivery date handling based on type
      if (deliveryDateType === 'Estimate') {
        // For Estimate, only send start and end dates
        if (formData.dates?.deliveryDateStart) {
          datesObj.deliveryDateStart = convertFormDateToUTC5(formData.dates.deliveryDateStart);
        }
        if (formData.dates?.deliveryDateEnd) {
          datesObj.deliveryDateEnd = convertFormDateToUTC5(formData.dates.deliveryDateEnd);
        }
      } else {
        // For Exact, only send deliveryDate (use only dates.deliveryDate, not delivery.date)
        if (formData.dates?.deliveryDate) {
          datesObj.deliveryDate = convertFormDateToUTC5(formData.dates.deliveryDate);
        }
      }
      
      return datesObj;
    })(),
    
    fees: safeArray(formData.fees || []).map(fee => ({
      type: safeString(fee.type || 'Detention'),
      carrierRate: safeString(fee.carrierRate || ''),
      customerRate: safeString(fee.customerRate || '')
    })).filter(fee => fee.type && fee.type.trim() !== ''),
    
    tonu: {
      enabled: Boolean(formData.tonu?.enabled),
      carrierRate: safeString(formData.tonu?.carrierRate || ''),
      customerRate: safeString(formData.tonu?.customerRate || '')
    },
    
    tracking: safeString(formData.tracking),
    bolDocuments: extractImageKeys(formData.bolDocuments || []),
    rateConfirmationDocuments: extractImageKeys(formData.rateConfirmationDocuments || []),
    documents: extractImageKeys(formData.documents || []),
    tempEntityId: safeString(formData.tempEntityId || ''),
    images: extractImageKeys(formData.images || []),
    pdfs: extractImageKeys(formData.pdfs || []),
    deletedCarrierImages: safeArray(formData.deletedCarrierImages || []).map(key => safeString(key)).filter(Boolean),
    deletedPickupImages: safeArray(formData.deletedPickupImages || []).map(key => safeString(key)).filter(Boolean),
    deletedDeliveryImages: safeArray(formData.deletedDeliveryImages || []).map(key => safeString(key)).filter(Boolean),
    deletedVehicleImages: safeArray(formData.deletedVehicleImages || []).map(key => safeString(key)).filter(Boolean),
    deletedFreightImages: safeArray(formData.deletedFreightImages || []).map(key => safeString(key)).filter(Boolean),
    deletedBolDocuments: safeArray(formData.deletedBolDocuments || []).map(key => safeString(key)).filter(Boolean),
    deletedRateConfirmationDocuments: safeArray(formData.deletedRateConfirmationDocuments || []).map(key => safeString(key)).filter(Boolean),
    deletedDocuments: safeArray(formData.deletedDocuments || []).map(key => safeString(key)).filter(Boolean),
  };

  // Ensure 'images' field is never included in vehicle or freight
  // Explicitly remove it if it somehow got added
  if (loadData.vehicle?.images !== undefined) {
    delete loadData.vehicle.images;
  }
  if (loadData.freight?.images !== undefined) {
    delete loadData.freight.images;
  }

  // Save people in separate fields (independent copies for this load)
  // loadCarrierPeople - independent copy of carrier.people for this specific load
  if (formData.carrier?.people && Array.isArray(formData.carrier.people) && formData.carrier.people.length > 0) {
    const preserveType = (t) => (t != null && String(t).trim() !== '') ? String(t).trim() : 'driver';
    loadData.loadCarrierPeople = safeArray(formData.carrier.people).map(person => ({
      type: preserveType(person.type),
      fullName: safeString(person.fullName || ''),
      email: safeString(person.email || ''),
      phoneNumber: safeString(person.phoneNumber || '')
    })).filter(person => person.fullName && person.fullName.trim() !== '');
  } else {
    loadData.loadCarrierPeople = [];
  }

  // loadCustomerRepresentativePeoples - independent copy of customer.representativePeoples for this specific load
  if (formData.customer?.representativePeoples && Array.isArray(formData.customer.representativePeoples) && formData.customer.representativePeoples.length > 0) {
    const mapped = safeArray(formData.customer.representativePeoples).map(person => ({
      fullName: safeString(person.fullName),
      email: safeString(person.email || ''),
      phoneNumber: safeString(person.phoneNumber || '')
    }));
    const filtered = filterEmptyArrayItems(mapped, {
      customFilter: (person) => person.fullName && person.fullName.trim() !== ''
    });
    loadData.loadCustomerRepresentativePeoples = filtered || [];
  } else {
    loadData.loadCustomerRepresentativePeoples = [];
  }

  return loadData;
};

/**
 * Optimized function to remove null, undefined, and empty fields from an object
 * Recursively processes nested objects and arrays
 * Preserves valid values like false, 0, and non-empty strings
 * 
 * Performance optimizations:
 * - Early returns for primitives
 * - Single pass through objects/arrays
 * - Minimal object creation
 */
export const removeEmptyFields = (obj) => {
  // Early return for null/undefined
  if (obj === null || obj === undefined) {
    return undefined;
  }

  // Early return for primitives (preserve false, 0, etc.)
  const type = typeof obj;
  if (type !== 'object') {
    // Skip empty strings
    if (type === 'string' && obj.trim() === '') {
      return undefined;
    }
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    const len = obj.length;
    if (len === 0) return undefined;
    
    const filtered = [];
    for (let i = 0; i < len; i++) {
      const cleaned = removeEmptyFields(obj[i]);
      if (cleaned !== undefined) {
        filtered.push(cleaned);
      }
    }
    return filtered.length > 0 ? filtered : undefined;
  }

  // Handle objects - use Object.keys for better performance
  const keys = Object.keys(obj);
  const keyLen = keys.length;
  if (keyLen === 0) return undefined;

  const cleaned = {};
  let hasValidFields = false;

  for (let i = 0; i < keyLen; i++) {
    const key = keys[i];
    const value = obj[key];
    
    // Skip null/undefined
    if (value === null || value === undefined) {
      continue;
    }

    // Skip empty strings (fast check)
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }

    // Explicitly exclude 'images' field from vehicle and freight objects
    // We only want vehicleImages and freightImages
    if (key === 'images' && (obj.shipment !== undefined || obj.specialRequirements !== undefined)) {
      // This is likely vehicle or freight object - skip 'images' field
      continue;
    }

    // Recursively clean nested structures
    const cleanedValue = removeEmptyFields(value);
    
    // Only add if cleaned value is not undefined
    if (cleanedValue !== undefined) {
      cleaned[key] = cleanedValue;
      hasValidFields = true;
    }
  }

  // Return undefined for empty objects
  return hasValidFields ? cleaned : undefined;
};

/**
 * Remove empty fields for PATCH requests
 * IMPORTANT: This function is called ONLY on dirty (changed) fields
 * It preserves empty values for clearable fields (to allow explicit clearing)
 * but removes empty values for other fields
 */
export const removeEmptyFieldsForPatch = (value) => {
  if (value === null || value === undefined) return undefined;

  // List of fields that can be explicitly cleared (empty string/null/empty array is meaningful)
  const clearableFields = [
    'customerRate', 
    'carrierRate', 
    'orderId', 
    'tracking', 
    'notes',
    'status',
    'paymentMethod',
    'paymentTerms',
    'customerEmails',
    'carrierEmails'
  ];

  // For arrays: preserve empty arrays only for clearable fields
  if (Array.isArray(value)) {
    // Empty arrays are preserved for clearable fields (emails clearing)
    // For other fields, empty arrays are removed (they weren't changed to empty)
    return value.length > 0 ? value : undefined;
  }

  // For primitives: preserve empty strings only for clearable fields
  if (typeof value !== "object") {
    if (typeof value === "string" && value.trim() === "") {
      return undefined; // Empty strings are removed - if field was cleared, it should be in clearableFields
    }
    return value;
  }

  const out = {};

  for (const [k, v] of Object.entries(value)) {
    // Preserve clearable fields even if empty (user explicitly cleared them)
    if (clearableFields.includes(k)) {
      if (Array.isArray(v)) {
        // For arrays, preserve even if empty (to allow clearing emails)
        out[k] = v;
      } else if (typeof v === 'string') {
        // For strings, preserve even if empty (to allow clearing)
        out[k] = v;
      } else if (v !== undefined && v !== null) {
        // For other types, preserve if not undefined/null
        out[k] = v;
      } else {
        // null/undefined for clearable fields - preserve null to allow clearing
        out[k] = v;
      }
    } else {
      // For non-clearable fields, recursively clean empty values
      const cleaned = removeEmptyFieldsForPatch(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
  }

  return Object.keys(out).length > 0 ? out : undefined;
};

/**
 * Validate load data before submission
 * @param {Object} formData - Form data to validate
 * @param {boolean} isUpdate - If true, skip required field validation (only validate changed data)
 * @param {boolean} skipCarrierValidation - If true, skip carrier validation (for step 4 submit)
 */
export const validateLoadData = (formData, isUpdate = false, skipCarrierValidation = false) => {
  const errors = [];

  // Skip required field validations for updates - we only send changed fields
  if (isUpdate) {
    // For updates, only validate data that is being sent
    // Insurance custom amount validation (if insurance type is being updated)
    const insuranceType = formData.insurance?.type || '';
    if ((insuranceType === 'Custom' || insuranceType === 'custom') && formData.insurance?.customAmount === '') {
      errors.push({ field: 'insurance.customAmount', message: 'Custom insurance amount is required' });
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  // --- CREATE MODE: Full validation ---
  
  // Validate type
  const type = formData.type || {};
  if (!type.freight && !type.vehicle) {
    errors.push({ field: 'type', message: 'Select Freight or Vehicle' });
  }

  // Validate vehicle shipments if vehicle type is selected
  if (type.vehicle) {
    const vehicleShipments = formData.vehicle?.shipment || [];
    if (vehicleShipments.length === 0) {
      errors.push({ field: 'vehicle.shipment', message: 'At least one vehicle shipment is required' });
    } else {
      vehicleShipments.forEach((vehicle, index) => {
        if (!vehicle.vin) {
          errors.push({ field: `vehicle.shipment[${index}].vin`, message: `VIN is required for Vehicles ${index + 1}` });
        }
      });
    }
  }

  // Validate freight shipments if freight type is selected
  if (type.freight) {
    const freightShipments = formData.freight?.shipment || [];
    if (freightShipments.length === 0) {
      errors.push({ field: 'freight.shipment', message: 'At least one freight shipment is required' });
    }
  }

  // Validate pickup location (nested address structure)
  if (!formData.pickup?.address?.city) {
    errors.push({ field: 'pickup.address.city', message: 'Pickup city is required' });
  }
  if (!formData.pickup?.address?.state) {
    errors.push({ field: 'pickup.address.state', message: 'Pickup state is required' });
  }

  // Validate delivery location (nested address structure)
  if (!formData.delivery?.address?.city) {
    errors.push({ field: 'delivery.address.city', message: 'Delivery city is required' });
  }
  if (!formData.delivery?.address?.state) {
    errors.push({ field: 'delivery.address.state', message: 'Delivery state is required' });
  }

  // Carrier information validation
  if (!skipCarrierValidation) {
    const carrierNameValue = formData.carrier?.name || formData.carrier?.companyName;
    if (!carrierNameValue) {
      errors.push({
        field: "carrier.name",
        message: "Carrier name is required",
      });
    }
    if (!formData.carrier?.phoneNumber) {
      errors.push({
        field: "carrier.phoneNumber",
        message: "Carrier phone number is required",
      });
    }
  }

  // Validate insurance custom amount
  const insuranceType = formData.insurance?.type || '';
  if ((insuranceType === 'Custom' || insuranceType === 'custom') && !formData.insurance?.customAmount) {
    errors.push({ field: 'insurance.customAmount', message: 'Custom insurance amount is required' });
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

/**
 * Transform API load data to form format
 * Converts API response structure to form defaultValues structure
 */
export const transformLoadDataForForm = (apiLoadData) => {
  if (!apiLoadData) return null;
  
  // Handle both nested (load.load) and flat structures
  const load = apiLoadData.load || apiLoadData;
  const standardPaymentMethods = [
    'ACH',
    'Wire',
    'Check',
    'Credit Card',
    'Cash',
    'Zelle',
    'Factoring',
    'Other'
  ];
  
  return {
    orderId: load.orderId || '',
    
    customer: {
      id: load.customer?._id || load.customer?.id || null,
      companyName: load.customer?.companyName || load.customer?.name || '',
      type: load.customer?.type || 'customer',
      email: load.customer?.email || '',
      customerAddress: {
        address: load.customer?.customerAddress?.address || load.customer?.address?.address || '',
        city: load.customer?.customerAddress?.city || load.customer?.address?.city || load.customer?.city || '',
        state: load.customer?.customerAddress?.state || load.customer?.address?.state || load.customer?.state || '',
        zipCode: load.customer?.customerAddress?.zipCode || load.customer?.address?.zipCode || load.customer?.zipCode || ''
      },
      phoneNumber: load.customer?.phoneNumber || load.customer?.phone || '',
      // Use loadCustomerRepresentativePeoples if exists (saved for this load), otherwise use customer.representativePeoples
      representativePeoples: Array.isArray(load.loadCustomerRepresentativePeoples) && load.loadCustomerRepresentativePeoples.length > 0
        ? load.loadCustomerRepresentativePeoples.map(person => ({
            fullName: person.fullName || '',
            email: person.email || '',
            phoneNumber: person.phoneNumber || ''
          }))
        : Array.isArray(load.customer?.representativePeoples) 
          ? load.customer.representativePeoples.map(person => ({
              fullName: person.fullName || '',
              email: person.email || '',
              phoneNumber: person.phoneNumber || ''
            }))
          : []
    },
    customerEmails: load.customerEmails || (load.customer?.email ? [load.customer.email] : []),
    customerRate: load.customerRate || '',
    carrierRate: load.carrierRate || '',
    
    type: {
      freight: Boolean(load.type?.freight),
      vehicle: Boolean(load.type?.vehicle)
    },
    
    vehicle: {
      shipment: load.vehicle?.shipment || [],
      specialRequirements: load.vehicle?.specialRequirements || '',
      vehicleImages: load.vehicle?.vehicleImages || []
    },
    
    freight: {
      shipment: (load.freight?.shipment || []).map(s => ({
        ...s,
        dimensionsUnit: s.dimensionsUnit === 'inches' ? 'inches' : 'feet',
        onPallets: s.onPallets === true ? 'yes' : 'no',
        shipmentUnits: s.shipmentUnits != null && s.shipmentUnits !== '' ? String(s.shipmentUnits) : 'N/A'
      })),
      freightImages: load.freight?.freightImages || []
    },
    
    pickup: {
      locationName: load.pickup?.locationName || '',
      address: {
        address: load.pickup?.address?.address || (typeof load.pickup?.address === 'string' ? load.pickup.address : '') || '',
        city: load.pickup?.address?.city || load.pickup?.city || '',
        state: load.pickup?.address?.state || load.pickup?.state || '',
        zipCode: load.pickup?.address?.zipCode || load.pickup?.zipCode || load.pickup?.zip || ''
      },
      contactPhone: load.pickup?.contactPhone || '',
      notes: load.pickup?.notes || '',
      date: load.pickup?.date || load.dates?.pickupDate || '',
      images: load.pickup?.images || []
    },
    
    delivery: {
      locationName: load.delivery?.locationName || '',
      address: {
        address: load.delivery?.address?.address || (typeof load.delivery?.address === 'string' ? load.delivery.address : '') || '',
        city: load.delivery?.address?.city || load.delivery?.city || '',
        state: load.delivery?.address?.state || load.delivery?.state || '',
        zipCode: load.delivery?.address?.zipCode || load.delivery?.zipCode || load.delivery?.zip || ''
      },
      contactPhone: load.delivery?.contactPhone || '',
      notes: load.delivery?.notes || '',
      date: load.delivery?.date || load.dates?.deliveryDate || '',
      images: load.delivery?.images || []
    },
    
    carrier: {
      id: load.carrier?._id || load.carrier?.id || null,
      name: load.carrier?.name || load.carrier?.companyName || '',
      // Use loadCarrierPeople if exists (saved for this load), otherwise use carrier.people
      people: Array.isArray(load.loadCarrierPeople) && load.loadCarrierPeople.length > 0
        ? load.loadCarrierPeople.map(person => ({
            type: (person.type != null && String(person.type).trim() !== '') ? String(person.type).trim() : 'driver',
            fullName: person.fullName || '',
            email: person.email || '',
            phoneNumber: person.phoneNumber || ''
          }))
        : Array.isArray(load.carrier?.people) 
          ? load.carrier.people.map(person => ({
              type: (person.type != null && String(person.type).trim() !== '') ? String(person.type).trim() : 'driver',
              fullName: person.fullName || '',
              email: person.email || '',
              phoneNumber: person.phoneNumber || ''
            }))
          : [],
      phoneNumber: load.carrier?.phoneNumber || load.carrier?.phone || '',
      email: load.carrier?.email || '',
      companyName: load.carrier?.companyName || load.carrier?.name || '',
      dba: load.carrier?.dba || '',
      mcNumber: load.carrier?.mcNumber || '',
      dotNumber: load.carrier?.dotNumber || '',
      address: {
        address: load.carrier?.address?.address || '',
        city: load.carrier?.address?.city || load.carrier?.city || '',
        state: load.carrier?.address?.state || load.carrier?.state || '',
        zipCode: load.carrier?.address?.zipCode || load.carrier?.zipCode || ''
      },
      equipment: Array.isArray(load.carrier?.equipment) && load.carrier.equipment.length > 0
        ? load.carrier.equipment
        : (() => {
            const equipmentType = Array.isArray(load.carrier?.equipmentType)
              ? load.carrier.equipmentType
              : load.carrier?.equipmentType
                ? [load.carrier.equipmentType]
                : [];
            const size = Array.isArray(load.carrier?.size)
              ? load.carrier.size
              : load.carrier?.size
                ? [load.carrier.size]
                : [];
            return equipmentType.map(type => ({ type, sizes: size }));
          })(),
      equipmentType: Array.isArray(load.carrier?.equipmentType) && load.carrier.equipmentType.length > 0 
        ? load.carrier.equipmentType 
        : load.carrier?.equipmentType 
          ? [load.carrier.equipmentType] 
          : [],
      size: Array.isArray(load.carrier?.size) && load.carrier.size.length > 0
        ? load.carrier.size
        : load.carrier?.size
          ? [load.carrier.size]
          : [],
      capabilities: load.carrier?.capabilities || [],
      certifications: load.carrier?.certifications || []
    },
    carrierEmails: load.carrierEmails || [],
    carrierPhotos: load.carrierPhotos || [],
    
    paymentMethod: standardPaymentMethods.includes(load.paymentMethod)
      ? load.paymentMethod
      : (load.paymentMethod ? 'Other' : ''),
    paymentMethodOther: standardPaymentMethods.includes(load.paymentMethod)
      ? ''
      : (load.paymentMethod || ''),
    paymentTerms: load.paymentTerms ? String(load.paymentTerms) : '',
    
    insurance: {
      type: load.insurance?.type || 'Motor Truck Cargo Coverage',
      customAmount: load.insurance?.customAmount || ''
    },
    
    status: load.status || 'Listed',
    
    dates: {
      assignedDate: load.dates?.assignedDate || '',
      deadline: load.dates?.deadline || '',
      pickupDate: load.dates?.pickupDate || load.pickup?.date || '',
      pickupDateType: load.dates?.pickupDateType || 'Exact',
      pickupDateStart: load.dates?.pickupDateStart || '',
      pickupDateEnd: load.dates?.pickupDateEnd || '',
      deliveryDate: load.dates?.deliveryDate || load.delivery?.date || '',
      deliveryDateType: load.dates?.deliveryDateType || 'Exact',
      deliveryDateStart: load.dates?.deliveryDateStart || '',
      deliveryDateEnd: load.dates?.deliveryDateEnd || '',
      aging: load.dates?.aging || ''
    },
    
    fees: Array.isArray(load.fees) 
      ? load.fees.map(fee => ({
          type: fee.type || 'Detention',
          carrierRate: fee.carrierRate || '',
          customerRate: fee.customerRate || '',
          total: fee.total || ''
        }))
      : [],
    
    tonu: {
      enabled: Boolean(load.tonu?.enabled),
      carrierRate: load.tonu?.carrierRate || '',
      customerRate: load.tonu?.customerRate || ''
    },
    
    tracking: load.tracking || '',
    bolDocuments: load.bolDocuments || [],
    rateConfirmationDocuments: load.rateConfirmationDocuments || [],
    documents: load.documents || [],
    tempEntityId: load.tempEntityId || '',
    images: load.images || [],
    pdfs: load.pdfs || [],
    deletedCarrierImages: [],
    deletedPickupImages: [],
    deletedDeliveryImages: [],
    deletedVehicleImages: [],
    deletedFreightImages: [],
    deletedBolDocuments: [],
    deletedRateConfirmationDocuments: [],
    deletedDocuments: [],
  };
};


